<!-- views/campaigns/index.vue -->
<template>
  <div class="campaign-list">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>广告系列管理</h2>
      <div class="header-actions">
        <el-button icon="el-icon-setting" @click="showColumnConfig = true">
          列设置
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">
          创建广告系列
        </el-button>
      </div>
    </div>

    <!-- 筛选栏 -->
    <el-card shadow="never" class="filter-card">
      <el-form :model="filter" inline>
        <el-form-item label="广告主">
          <el-select
            v-model="filter.advertiser_id"
            placeholder="请选择广告主"
            clearable
            style="width: 380px"
            @change="handleAdvertiserChange"
          >
            <el-option
              v-for="item in advertisers"
              :key="item.advertiser_id"
              :label="`${item.advertiser_name} (${item.advertiser_id})`"
              :value="item.advertiser_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="系列名称">
          <el-input
              v-model="filter.campaign_name"
              placeholder="请输入系列名称"
              clearable
              style="width: 200px"
          />
        </el-form-item>

<!--        <el-form-item label="广告目标">-->
<!--          <el-select-->
<!--              v-model="filter.objective_type"-->
<!--              placeholder="全部"-->
<!--              clearable-->
<!--              style="width: 140px"-->
<!--          >-->
<!--            <el-option-->
<!--                v-for="item in objectiveOptions"-->
<!--                :key="item.value"-->
<!--                :label="item.label"-->
<!--                :value="item.value"-->
<!--            />-->
<!--          </el-select>-->
<!--        </el-form-item>-->

<!--        <el-form-item label="创建时间">-->
<!--          <el-date-picker-->
<!--              v-model="dateRange"-->
<!--              type="daterange"-->
<!--              range-separator="至"-->
<!--              start-placeholder="开始日期"-->
<!--              end-placeholder="结束日期"-->
<!--              value-format="yyyy-MM-dd 00:00:00"-->
<!--              style="width: 280px"-->
<!--          />-->
<!--        </el-form-item>-->

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never">
      <el-table
          :data="campaigns"
          v-loading="loading"
          stripe
          max-height="800"
          style="width: 100%"
          @sort-change="handleSortChange"
      >
        <!-- 动态列 -->
        <template v-for="column in visibleColumns">
          <!-- 特殊渲染的列 -->
          <el-table-column
              v-if="column.prop === 'campaign_id'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              :sortable="column.sortable"
          >
            <template slot-scope="{ row }">
              <el-tooltip :content="row.campaign_id" placement="top">
                <span class="id-text">{{ row.campaign_id | truncate(16) }}</span>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'campaign_name'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :min-width="column.minWidth"
          >
            <template slot-scope="{ row }">
              <span class="campaign-name">{{ row.campaign_name }}</span>
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'objective_type'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
          >
            <template slot-scope="{ row }">
              {{ getObjectiveLabel(row.objective_type) }}
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'budget'"
              :key="column.prop"
              :label="column.label"
              :width="column.width"
              sortable="custom"
              prop="budget"
          >
            <template slot-scope="{ row }">
              <div v-if="row.budget_mode === 'BUDGET_MODE_INFINITE'">
                无限预算
              </div>
              <div v-else>
                <div>${{ formatBudget(row.budget) }}</div>
                <div class="budget-mode">{{ getBudgetModeLabel(row.budget_mode) }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'smart_plus_adgroup_mode'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
          >
            <template slot-scope="{ row }">
              <el-tag
                  :type="row.smart_plus_adgroup_mode === 'MULTIPLE' ? 'primary' : 'info'"
                  size="small"
              >
                {{ getAdgroupModeLabel(row.smart_plus_adgroup_mode) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'operation_status'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
          >
            <template slot-scope="{ row }">
              <status-tag :status="row.operation_status" />
            </template>
          </el-table-column>

          <el-table-column
              v-else-if="column.prop === 'create_time'"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              sortable="custom"
          >
            <template slot-scope="{ row }">
              <div class="time-text">{{ formatTime(row.create_time) }}</div>
            </template>
          </el-table-column>

          <!-- 指标列 -->
          <el-table-column
              v-else-if="metricColumnsMap[column.prop]"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              :sortable="column.sortable"
          >
            <template slot-scope="{ row }">
              <div class="metric-value">{{ formatMetric(row[column.prop], column.prop) }}</div>
            </template>
          </el-table-column>

          <!-- 普通文本列 -->
          <el-table-column
              v-else
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              :min-width="column.minWidth"
              :sortable="column.sortable"
          />
        </template>

        <!-- 操作列（固定在最右侧） -->
        <el-table-column label="操作" width="220" >
          <template slot-scope="{ row }">
            <el-button
                type="text"
                size="small"
                @click="handleEdit(row)"
            >
              编辑
            </el-button>
            <el-button
                v-if="row.operation_status === 'ENABLE'"
                type="text"
                size="small"
                @click="handlePause(row)"
            >
              暂停
            </el-button>
            <el-button
                v-else-if="row.operation_status === 'DISABLE'"
                type="text"
                size="small"
                @click="handleResume(row)"
            >
              启用
            </el-button>
            <el-button
                type="text"
                size="small"
                style="color: #f56c6c"
                @click="handleDelete(row)"
            >
              删除
            </el-button>
            <el-button
                type="text"
                size="small"
                @click="handleViewAdgroups(row)"
            >
              查看广告组
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
            background
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 列配置弹窗 -->
    <el-dialog
        title="列设置"
        :visible.sync="showColumnConfig"
        width="600px"
    >
      <div class="column-config">
        <div class="column-config-section">
          <h4>基础信息</h4>
          <el-checkbox-group v-model="selectedBaseColumns">
            <div v-for="col in baseColumns" :key="col.prop" class="column-checkbox">
              <el-checkbox :label="col.prop">{{ col.label }}</el-checkbox>
              <el-button
                  v-if="col.fixed !== undefined"
                  type="text"
                  size="small"
                  @click="toggleFixed(col.prop)"
              >
                {{ col.fixed === 'right' ? '取消固定' : '固定右侧' }}
              </el-button>
            </div>
          </el-checkbox-group>
        </div>

        <div class="column-config-section">
          <h4>核心指标</h4>
          <el-checkbox-group v-model="selectedMetricColumns">
            <div v-for="col in metricColumns" :key="col.prop" class="column-checkbox">
              <el-checkbox :label="col.prop">
                {{ col.label }}
                <span class="metric-desc">{{ col.description }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>

        <div class="column-config-section">
          <h4>互动指标</h4>
          <el-checkbox-group v-model="selectedInteractionColumns">
            <div v-for="col in interactionColumns" :key="col.prop" class="column-checkbox">
              <el-checkbox :label="col.prop">
                {{ col.label }}
                <span class="metric-desc">{{ col.description }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>

        <div class="column-config-section">
          <h4>视频指标</h4>
          <el-checkbox-group v-model="selectedVideoColumns">
            <div v-for="col in videoColumns" :key="col.prop" class="column-checkbox">
              <el-checkbox :label="col.prop">
                {{ col.label }}
                <span class="metric-desc">{{ col.description }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>

        <div class="column-config-section">
          <h4>转化指标</h4>
          <el-checkbox-group v-model="selectedConversionColumns">
            <div v-for="col in conversionColumns" :key="col.prop" class="column-checkbox">
              <el-checkbox :label="col.prop">
                {{ col.label }}
                <span class="metric-desc">{{ col.description }}</span>
              </el-checkbox>
            </div>
          </el-checkbox-group>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="resetColumnConfig">重置默认</el-button>
        <el-button type="primary" @click="saveColumnConfig">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="600px"
        @close="handleDialogClose"
        class="campaign-dialog"
    >
      <el-form
          ref="campaignForm"
          :model="formData"
          :rules="formRules"
          label-width="120px"
      >
        <!-- ========== 基础信息 ========== -->
        <div class="form-section">
          <div class="section-title">基础信息</div>

          <el-form-item label="广告主" prop="advertiser_id">
            <el-select
                v-model="formData.advertiser_id"
                placeholder="请选择广告主"              style="width: 100%"
                :disabled="isEdit"
            >
              <el-option
                  v-for="item in advertisers"
                  :key="item.advertiser_id"
                  :label="`${item.advertiser_name} (${item.advertiser_id})`"
                  :value="item.advertiser_id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="系列名称" prop="campaign_name" required>
            <el-input
                v-model="formData.campaign_name"
                placeholder="请输入系列名称"
                maxlength="512"
                show-word-limit
            />
          </el-form-item>

          <el-form-item label="广告目标" prop="objective_type" required>
            <el-select
                v-model="formData.objective_type"
                placeholder="请选择广告目标"                style="width: 100%"
            >
              <el-option
                  v-for="item in objectiveOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="应用推广类型" prop="app_promotion_type" v-if="formData.objective_type === 'APP_PROMOTION'">
            <el-select
                v-model="formData.app_promotion_type"
                placeholder="请选择应用推广类型"                style="width: 100%"
            >
              <el-option
                  v-for="item in appPromotionOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="广告系列类型" prop="campaign_type">
            <el-radio-group v-model="formData.campaign_type">
              <el-radio label="REGULAR_CAMPAIGN">常规广告系列</el-radio>
              <!--              <el-radio label="IOS14_CAMPAIGN">iOS 14 广告系列</el-radio>-->
            </el-radio-group>
          </el-form-item>
        </div>

        <!-- ========== 预算设置 ========== -->
        <div class="form-section">
          <div class="section-title">预算设置</div>

          <el-form-item label="预算模式" prop="budget_mode" required>
            <el-radio-group v-model="formData.budget_mode">
              <el-radio label="BUDGET_MODE_DYNAMIC_DAILY_BUDGET">动态每日预算</el-radio>
              <!--              <el-radio label="BUDGET_MODE_TOTAL">终身预算</el-radio>-->
            </el-radio-group>
          </el-form-item>

          <el-form-item
              label="预算金额"
              prop="budget"
              required
          >
            <budget-input
                v-model="formData.budget"
                :min-budget="getMinBudget()"
                :step="getBudgetStep()"
            />
            <div class="form-tip">预算单位为元，¥{{ ((formData.budget || 0) ).toFixed(2) }}元</div>
          </el-form-item>
        </div>

        <!-- 提交按钮 -->
        <div class="form-actions">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
            {{ isEdit ? '保存修改' : '创建广告系列' }}
          </el-button>
        </div>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
import { campaignApi } from '@/api/campaign'
import { advertiserApi } from '@/api/advertiserApi'
import StatusTag from '@/components/StatusTag'
import BudgetInput from '@/components/BudgetInput'
import {
  OBJECTIVE_TYPE,
  OBJECTIVE_LABEL,
  BUDGET_MODE_LABEL,
  ADGROUP_MODE_LABEL,
  // 指标相关常量
  METRICS,
  METRIC_LABELS,
  METRIC_DESCRIPTIONS,
  METRIC_CATEGORIES
} from '@/utils/constants'
import {appApi as catalogApi} from "@/api/app";

// 基础列定义
const BASE_COLUMNS = [
  { prop: 'campaign_id', label: '系列ID', width: 180, sortable: false, fixed: 'left' },
  { prop: 'campaign_name', label: '系列名称', minWidth: 200, sortable: false },
  { prop: 'objective_type', label: '广告目标', width: 120, sortable: false },
  { prop: 'budget', label: '预算', width: 150, sortable: true },
  { prop: 'smart_plus_adgroup_mode', label: '模式', width: 100, sortable: false },
  { prop: 'operation_status', label: '状态', width: 100, sortable: false },
  { prop: 'create_time', label: '创建时间', width: 160, sortable: true }
]

// 核心指标列
const CORE_METRIC_COLUMNS = [
  { prop: METRICS.SPEND, label: METRIC_LABELS[METRICS.SPEND], description: METRIC_DESCRIPTIONS[METRICS.SPEND], width: 120, sortable: true, category: 'core' },
  { prop: METRICS.IMPRESSIONS, label: METRIC_LABELS[METRICS.IMPRESSIONS], description: METRIC_DESCRIPTIONS[METRICS.IMPRESSIONS], width: 120, sortable: true, category: 'core' },
  { prop: METRICS.CLICKS, label: METRIC_LABELS[METRICS.CLICKS], description: METRIC_DESCRIPTIONS[METRICS.CLICKS], width: 100, sortable: true, category: 'core' },
  { prop: METRICS.CTR, label: METRIC_LABELS[METRICS.CTR], description: METRIC_DESCRIPTIONS[METRICS.CTR], width: 100, sortable: true, category: 'core' },
  { prop: METRICS.CPC, label: METRIC_LABELS[METRICS.CPC], description: METRIC_DESCRIPTIONS[METRICS.CPC], width: 100, sortable: true, category: 'core' },
  { prop: METRICS.CPM, label: METRIC_LABELS[METRICS.CPM], description: METRIC_DESCRIPTIONS[METRICS.CPM], width: 100, sortable: true, category: 'core' },
  { prop: METRICS.CONVERSION, label: METRIC_LABELS[METRICS.CONVERSION], description: METRIC_DESCRIPTIONS[METRICS.CONVERSION], width: 100, sortable: true, category: 'core' },
  { prop: METRICS.COST_PER_CONVERSION, label: METRIC_LABELS[METRICS.COST_PER_CONVERSION], description: METRIC_DESCRIPTIONS[METRICS.COST_PER_CONVERSION], width: 120, sortable: true, category: 'core' }
]

// 互动指标列
const INTERACTION_METRIC_COLUMNS = [
  { prop: METRICS.ENGAGEMENTS, label: METRIC_LABELS[METRICS.ENGAGEMENTS], description: METRIC_DESCRIPTIONS[METRICS.ENGAGEMENTS], width: 100, sortable: true, category: 'interaction' },
  { prop: METRICS.LIKES, label: METRIC_LABELS[METRICS.LIKES], description: METRIC_DESCRIPTIONS[METRICS.LIKES], width: 80, sortable: true, category: 'interaction' },
  { prop: METRICS.COMMENTS, label: METRIC_LABELS[METRICS.COMMENTS], description: METRIC_DESCRIPTIONS[METRICS.COMMENTS], width: 80, sortable: true, category: 'interaction' },
  { prop: METRICS.SHARES, label: METRIC_LABELS[METRICS.SHARES], description: METRIC_DESCRIPTIONS[METRICS.SHARES], width: 80, sortable: true, category: 'interaction' },
  { prop: METRICS.FOLLOWS, label: METRIC_LABELS[METRICS.FOLLOWS], description: METRIC_DESCRIPTIONS[METRICS.FOLLOWS], width: 80, sortable: true, category: 'interaction' },
  { prop: METRICS.PROFILE_VISITS, label: METRIC_LABELS[METRICS.PROFILE_VISITS], description: METRIC_DESCRIPTIONS[METRICS.PROFILE_VISITS], width: 100, sortable: true, category: 'interaction' }
]

// 视频指标列
const VIDEO_METRIC_COLUMNS = [
  { prop: METRICS.VIDEO_PLAY_ACTIONS, label: METRIC_LABELS[METRICS.VIDEO_PLAY_ACTIONS], description: METRIC_DESCRIPTIONS[METRICS.VIDEO_PLAY_ACTIONS], width: 100, sortable: true, category: 'video' },
  { prop: METRICS.VIDEO_WATCHED_2S, label: METRIC_LABELS[METRICS.VIDEO_WATCHED_2S], description: METRIC_DESCRIPTIONS[METRICS.VIDEO_WATCHED_2S], width: 100, sortable: true, category: 'video' },
  { prop: METRICS.VIDEO_WATCHED_6S, label: METRIC_LABELS[METRICS.VIDEO_WATCHED_6S], description: METRIC_DESCRIPTIONS[METRICS.VIDEO_WATCHED_6S], width: 100, sortable: true, category: 'video' },
  { prop: METRICS.VIDEO_VIEWS_P50, label: METRIC_LABELS[METRICS.VIDEO_VIEWS_P50], description: METRIC_DESCRIPTIONS[METRICS.VIDEO_VIEWS_P50], width: 100, sortable: true, category: 'video' },
  { prop: METRICS.VIDEO_VIEWS_P100, label: METRIC_LABELS[METRICS.VIDEO_VIEWS_P100], description: METRIC_DESCRIPTIONS[METRICS.VIDEO_VIEWS_P100], width: 100, sortable: true, category: 'video' },
  { prop: METRICS.AVERAGE_VIDEO_PLAY, label: METRIC_LABELS[METRICS.AVERAGE_VIDEO_PLAY], description: METRIC_DESCRIPTIONS[METRICS.AVERAGE_VIDEO_PLAY], width: 120, sortable: true, category: 'video' }
]

// 转化指标列
const CONVERSION_METRIC_COLUMNS = [
  { prop: METRICS.APP_INSTALL, label: METRIC_LABELS[METRICS.APP_INSTALL], description: METRIC_DESCRIPTIONS[METRICS.APP_INSTALL], width: 100, sortable: true, category: 'conversion' },
  { prop: METRICS.COST_PER_APP_INSTALL, label: METRIC_LABELS[METRICS.COST_PER_APP_INSTALL], description: METRIC_DESCRIPTIONS[METRICS.COST_PER_APP_INSTALL], width: 120, sortable: true, category: 'conversion' },
  { prop: METRICS.COMPLETE_PAYMENT, label: METRIC_LABELS[METRICS.COMPLETE_PAYMENT], description: METRIC_DESCRIPTIONS[METRICS.COMPLETE_PAYMENT], width: 100, sortable: true, category: 'conversion' },
  { prop: METRICS.COST_PER_COMPLETE_PAYMENT, label: METRIC_LABELS[METRICS.COST_PER_COMPLETE_PAYMENT], description: METRIC_DESCRIPTIONS[METRICS.COST_PER_COMPLETE_PAYMENT], width: 120, sortable: true, category: 'conversion' },
  { prop: METRICS.PURCHASE_ROAS, label: METRIC_LABELS[METRICS.PURCHASE_ROAS], description: METRIC_DESCRIPTIONS[METRICS.PURCHASE_ROAS], width: 100, sortable: true, category: 'conversion' },
  { prop: METRICS.REGISTRATION, label: METRIC_LABELS[METRICS.REGISTRATION], description: METRIC_DESCRIPTIONS[METRICS.REGISTRATION], width: 100, sortable: true, category: 'conversion' },
  { prop: METRICS.COST_PER_REGISTRATION, label: METRIC_LABELS[METRICS.COST_PER_REGISTRATION], description: METRIC_DESCRIPTIONS[METRICS.COST_PER_REGISTRATION], width: 120, sortable: true, category: 'conversion' }
]

// 默认选中的基础列
const DEFAULT_BASE_COLUMNS = ['campaign_id', 'campaign_name', 'objective_type', 'budget', 'operation_status', 'create_time']

// 存储键名
const STORAGE_KEY = 'campaign_column_config'

export default {
  name: 'CampaignList',
  components: {
    StatusTag,
    BudgetInput
  },
  data() {
    return {
      advertisers: [],
      campaigns: [],
      loading: false,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      sortField: '',
      sortOrder: '',
      filter: {
        advertiser_id: localStorage.getItem('advertiser_id') || '',
        campaign_name: '',
        objective_type: ''
      },
      dateRange: [],
      objectiveOptions: [
        { value: OBJECTIVE_TYPE.APP_PROMOTION, label: '应用推广' }
      ],

      // ========== 新增：应用推广相关选项 ==========
      appPromotionOptions: [
        { value: 'APP_INSTALL', label: '应用安装' },
        { value: 'APP_RETARGETING', label: '应用重定向' },
        { value: 'MINIS', label: 'TikTok小程序' }
      ],

      // ========== 新增：特殊行业选项 ==========
      specialIndustriesOptions: [
        { value: 'HOUSING', label: '房地产' },
        { value: 'EMPLOYMENT', label: '招聘' },
        { value: 'CREDIT', label: '信贷' }
      ],

      // ========== 新增：SKAN 回传窗口选项 ==========
      postbackWindowOptions: [
        { value: 'POSTBACK_WINDOW_MODE1', label: '模式1（0-2天，等待4天）' },
        { value: 'POSTBACK_WINDOW_MODE2', label: '模式2（3-7天，等待13天）' },
        { value: 'POSTBACK_WINDOW_MODE3', label: '模式3（8-35天，等待41天）' }
      ],

      // ========== 动态数据 ==========
      appOptions: [],
      minisOptions: [],
      catalogOptions: [],

      // 列配置
      showColumnConfig: false,
      baseColumns: BASE_COLUMNS,
      metricColumns: [...CORE_METRIC_COLUMNS, ...INTERACTION_METRIC_COLUMNS, ...VIDEO_METRIC_COLUMNS, ...CONVERSION_METRIC_COLUMNS],
      coreMetrics: CORE_METRIC_COLUMNS,
      interactionMetrics: INTERACTION_METRIC_COLUMNS,
      videoMetrics: VIDEO_METRIC_COLUMNS,
      conversionMetrics: CONVERSION_METRIC_COLUMNS,
      selectedBaseColumns: [...DEFAULT_BASE_COLUMNS],
      selectedMetricColumns: [],
      selectedCoreColumns: [],
      selectedInteractionColumns: [],
      selectedVideoColumns: [],
      selectedConversionColumns: [],
      columnFixedMap: {},

      // 弹窗相关
      dialogVisible: false,
      dialogTitle: '创建广告系列',
      submitLoading: false,
      isEdit: false,
      editCampaignId: null,
      // 表单数据（简化版）
      formData: {
        advertiser_id: '',
        campaign_name: '',
        objective_type: '',
        app_promotion_type: '',
        campaign_type: 'REGULAR_CAMPAIGN',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        budget: 10,
      },

      // 表单验证规则（简化版）
      formRules: {
        advertiser_id: [
          { required: true, message: '请选择广告主', trigger: 'change' }
        ],
        campaign_name: [
          { required: true, message: '请输入系列名称', trigger: 'blur' },
          { max: 512, message: '名称不能超过 512 个字符', trigger: 'blur' }
        ],
        objective_type: [
          { required: true, message: '请选择广告目标', trigger: 'change' }
        ],
        budget_mode: [
          { required: true, message: '请选择预算模式', trigger: 'change' }
        ],
        budget: [
          { required: true, message: '请输入预算金额', trigger: 'blur' },
          { type: 'number', min: 10, message: '预算至少为 10元（¥10）', trigger: 'blur' }
        ]
      },
      // 辅助变量（用于开关转换）
      gamingComplianceAgreement: 'OFF',
      appProfilePageState: 'OFF',
      budgetAutoAdjustEnabled: 'UNSET',

    }
  },  watch: {
    // 辅助变量同步到 formData
    gamingComplianceAgreement(val) {
      this.formData.gaming_ad_compliance_agreement = val
    },
    appProfilePageState(val) {
      this.formData.campaign_app_profile_page_state = val
    },
    budgetAutoAdjustEnabled(val) {
      this.formData.budget_auto_adjust_strategy = val
    }
  },
  computed: {
    // 所有选中的列
    selectedColumns() {
      return [...this.selectedBaseColumns, ...this.selectedMetricColumns]
    },
    // 可见列（根据选中状态和固定配置）
    visibleColumns() {
      const columns = []
      const baseCols = this.baseColumns.filter(col => this.selectedBaseColumns.includes(col.prop))
      const metricCols = this.metricColumns.filter(col => this.selectedMetricColumns.includes(col.prop))

      // 应用固定配置
      const allCols = [...baseCols, ...metricCols]
      const fixedRightCols = []
      const normalCols = []

      allCols.forEach(col => {
        if (this.columnFixedMap[col.prop] === 'right') {
          fixedRightCols.push(col)
        } else {
          normalCols.push(col)
        }
      })

      return [...normalCols, ...fixedRightCols]
    },
    // 指标列映射（用于快速查找指标类型）
    metricColumnsMap() {
      const map = {}
      this.metricColumns.forEach(col => {
        map[col.prop] = col
      })
      return map
    }
  },
  mounted() {
    this.loadColumnConfig()
    this.loadAdvertisers()
    if (this.filter.advertiser_id) {
      this.fetchCampaigns()
    }
  },
  methods: {
    // 加载广告主列表
    async loadAdvertisers() {
      try {
        const res = await advertiserApi.getList({ appid: "fff", secret:"ggg" })
        if (res.code === 0) {
          this.advertisers = res.data.list || []
          // 如果没有默认选中的广告主，自动选择第一个
          if (!this.filter.advertiser_id && this.advertisers.length > 0) {
            this.$set(this.filter, 'advertiser_id', this.advertisers[0].advertiser_id)
          }
        }
      } catch (error) {
        console.error('加载广告主列表失败', error)
      }
    },

    handleAdvertiserChange() {
      // 保存选中的广告主 ID 到 localStorage
      if (this.filter.advertiser_id) {
        localStorage.setItem('advertiser_id', this.filter.advertiser_id)
      } else {
        localStorage.removeItem('advertiser_id')
      }
      this.handleSearch()
    },

    async fetchCampaigns() {
      this.loading = true
      try {
        const advertiserId = this.filter.advertiser_id || localStorage.getItem('advertiser_id')
        const params = {
          advertiserId,
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          campaignName: this.filter.campaign_name,
          objectiveType: this.filter.objective_type,
          fields: this.getRequestFields()
        }

        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = this.dateRange[0]
          params.endDate = this.dateRange[1]
        }

        if (this.sortField) {
          params.sortField = this.sortField
          params.sortOrder = this.sortOrder
        }

        const res = await campaignApi.getList(params)
        if (res.code === 0) {
          this.campaigns = res.data.list || []
          this.pagination.total = res.data.pageInfo?.totalNumber || 0
        }
      } catch (error) {
        console.error('获取列表失败', error)
      } finally {
        this.loading = false
      }
    },

    // 获取请求字段列表
    getRequestFields() {
      // 基础字段始终需要
      const baseFields = ['campaign_id', 'campaign_name', 'objective_type', 'budget', 'budget_mode', 'smart_plus_adgroup_mode', 'operation_status', 'create_time']
      // 添加选中的指标字段
      const metricFields = [...this.selectedMetricColumns]
      // 去重
      return [...new Set([...baseFields, ...metricFields])]
    },
    // ========== 新增：加载应用列表 ==========
    async loadAppOptions() {
      try {
        const advertiserId = localStorage.getItem('advertiser_id')
        const res = await catalogApi.getMinisList({ advertiserId })
        if (res.code === 0) {
          this.appOptions = res.data.list || []
        }
      } catch (error) {
        console.error('加载应用列表失败', error)
      }
    },    // ========== 新增：加载小程序列表 ==========
    async loadMinisOptions() {
      try {
        const advertiserId = localStorage.getItem('advertiser_id')
        const res = await catalogApi.getMinisList({ advertiserId, page: 1, pageSize: 50 })
        if (res.code === 0) {
          this.minisOptions = (res.data.list || []).filter(m => m.minis_status === 'ACTIVE')
        }
      } catch (error) {
        console.error('加载小程序列表失败', error)
      }
    },

    // ========== 新增：加载目录列表 ==========
    async loadCatalogOptions() {
      try {
        const advertiserId = localStorage.getItem('advertiser_id')
        const res = await catalogApi.getList({ advertiserId })
        if (res.code === 0) {
          this.catalogOptions = res.data.list || []
        }
      } catch (error) {
        console.error('加载目录列表失败', error)
      }
    },
    // ========== 新增：获取最小预算（根据币种） ==========
    getMinBudget() {
      return 10
    },

    // ========== 新增：获取预算步长 ==========
    getBudgetStep() {
      return 10
    },
// ========== 表单联动方法 ==========
    handleObjectiveTypeChange(value) {
      // 重置相关字段
      if (value === 'APP_PROMOTION') {
        this.formData.sales_destination = null
        this.formData.catalog_enabled = false
        this.formData.is_promotional_campaign = false
      } else if (value === 'WEB_CONVERSIONS') {
        this.formData.app_promotion_type = null
        this.formData.app_id = ''
        this.formData.minis_id = ''
      } else if (value === 'LEAD_GENERATION') {
        this.formData.app_promotion_type = null
        this.formData.app_id = ''
        this.formData.sales_destination = null
      }
    },

    handleAppPromotionTypeChange(value) {
      if (value === 'MINIS') {
        this.formData.app_id = ''
        this.formData.campaign_type = 'REGULAR_CAMPAIGN'
        this.loadMinisOptions()
      } else {
        this.formData.minis_id = ''
      }
    },

    handleSalesDestinationChange(value) {
      if (value === 'APP') {
        this.formData.catalog_enabled = true
      } else {
        this.formData.catalog_enabled = false
        this.formData.catalog_type = null
        this.formData.catalog_id = ''
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchCampaigns()
    },

    handleReset() {
      this.filter = {
        advertiser_id: localStorage.getItem('advertiser_id') || '',
        campaign_name: '',
        objective_type: ''
      }
      this.dateRange = []
      this.pagination.page = 1
      this.fetchCampaigns()
    },

    handlePageChange(page) {
      this.pagination.page = page
      this.fetchCampaigns()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchCampaigns()
    },

    handleSortChange({ prop, order }) {
      if (order === 'ascending') {
        this.sortField = prop
        this.sortOrder = 'ASC'
      } else if (order === 'descending') {
        this.sortField = prop
        this.sortOrder = 'DESC'
      } else {
        this.sortField = ''
        this.sortOrder = ''
      }
      this.pagination.page = 1
      this.fetchCampaigns()
    },

    // 列配置相关方法
    loadColumnConfig() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          const config = JSON.parse(saved)
          this.selectedBaseColumns = config.selectedBaseColumns || DEFAULT_BASE_COLUMNS
          this.selectedMetricColumns = config.selectedMetricColumns || []
          this.columnFixedMap = config.columnFixedMap || {}
          this.syncMetricSubGroups()
        } else {
          this.resetColumnConfig()
        }
      } catch (e) {
        console.error('加载列配置失败', e)
        this.resetColumnConfig()
      }
    },

    syncMetricSubGroups() {
      // 根据选中的指标列同步各分组
      this.selectedCoreColumns = this.selectedMetricColumns.filter(col =>
          this.coreMetrics.some(m => m.prop === col)
      )
      this.selectedInteractionColumns = this.selectedMetricColumns.filter(col =>
          this.interactionMetrics.some(m => m.prop === col)
      )
      this.selectedVideoColumns = this.selectedMetricColumns.filter(col =>
          this.videoMetrics.some(m => m.prop === col)
      )
      this.selectedConversionColumns = this.selectedMetricColumns.filter(col =>
          this.conversionMetrics.some(m => m.prop === col)
      )
    },

    saveColumnConfig() {
      // 合并所有选中的指标
      const allMetricSelections = [
        ...this.selectedCoreColumns,
        ...this.selectedInteractionColumns,
        ...this.selectedVideoColumns,
        ...this.selectedConversionColumns
      ]
      this.selectedMetricColumns = [...new Set(allMetricSelections)]

      const config = {
        selectedBaseColumns: this.selectedBaseColumns,
        selectedMetricColumns: this.selectedMetricColumns,
        columnFixedMap: this.columnFixedMap
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
      this.showColumnConfig = false
      this.$message.success('列配置已保存')
      this.fetchCampaigns()
    },

    resetColumnConfig() {
      this.selectedBaseColumns = [...DEFAULT_BASE_COLUMNS]
      this.selectedMetricColumns = []
      this.selectedCoreColumns = []
      this.selectedInteractionColumns = []
      this.selectedVideoColumns = []
      this.selectedConversionColumns = []
      this.columnFixedMap = {}
      this.$message.success('已重置为默认配置')
    },

    toggleFixed(prop) {
      if (this.columnFixedMap[prop] === 'right') {
        this.$set(this.columnFixedMap, prop, undefined)
      } else {
        this.$set(this.columnFixedMap, prop, 'right')
      }
    },

    // 格式化指标值
    formatMetric(value, metricKey) {
      if (value === undefined || value === null) return '-'

      // 根据指标类型格式化
      if (metricKey === METRICS.CTR || metricKey === METRICS.CONVERSION_RATE_V2 ||
          metricKey === METRICS.PURCHASE_ROAS || metricKey === METRICS.SKAN_RESULT_RATE) {
        return `${(value * 100).toFixed(2)}%`
      }

      if (metricKey === METRICS.CPC || metricKey === METRICS.CPM ||
          metricKey === METRICS.COST_PER_CONVERSION || metricKey === METRICS.COST_PER_APP_INSTALL ||
          metricKey === METRICS.COST_PER_COMPLETE_PAYMENT || metricKey === METRICS.COST_PER_REGISTRATION) {
        return `¥${(value / 100).toFixed(2)}`
      }

      if (metricKey === METRICS.AVERAGE_VIDEO_PLAY) {
        return `${value.toFixed(1)}s`
      }

      // 大数格式化
      if (typeof value === 'number' && value >= 10000) {
        if (value >= 100000000) {
          return `${(value / 100000000).toFixed(1)}亿`
        }
        if (value >= 10000) {
          return `${(value / 10000).toFixed(1)}万`
        }
      }

      return value.toLocaleString?.() || value
    },

    // ========== 创建/编辑方法（简化版） ==========
    handleCreate() {
      this.isEdit = false
      this.dialogTitle = '创建广告系列'
      // 重置表单数据
      this.formData = {
        advertiser_id: this.filter.advertiser_id || localStorage.getItem('advertiser_id') || '',
        campaign_name: '',
        objective_type: '',
        app_promotion_type: '',
        campaign_type: 'REGULAR_CAMPAIGN',
        budget_mode: 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        budget: 50,
      }

      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.campaignForm && this.$refs.campaignForm.clearValidate()
      })
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑广告系列'
      this.editCampaignId = row.campaign_id

      // 填充表单数据（从现有数据中恢复）
      this.formData = {
        advertiser_id: row.advertiser_id || this.filter.advertiser_id,
        campaign_name: row.campaign_name,
        operation_status: row.operation_status || 'ENABLE',
        objective_type: row.objective_type,
        app_promotion_type: row.app_promotion_type || null,
        app_id: row.app_id || '',
        minis_id: row.minis_id || '',
        sales_destination: row.sales_destination || 'WEBSITE',
        catalog_enabled: row.catalog_enabled || false,
        catalog_type: row.catalog_type || null,
        catalog_id: row.catalog_id || '',
        catalog_authorized_bc_id: row.catalog_authorized_bc_id || '',
        is_promotional_campaign: row.is_promotional_campaign || false,
        movie_premiere_date: row.movie_premiere_date || '',
        campaign_type: row.campaign_type || 'REGULAR_CAMPAIGN',
        budget_optimize_on: row.budget_optimize_on ?? true,
        budget_mode: row.budget_mode || 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET',
        budget: row.budget || 50000,
        budget_auto_adjust_strategy: row.budget_auto_adjust_strategy || 'UNSET',
        gaming_ad_compliance_agreement: row.gaming_ad_compliance_agreement || 'OFF',
        campaign_app_profile_page_state: row.campaign_app_profile_page_state || 'OFF',
        campaign_app_profile_page_id: row.campaign_app_profile_page_id || '',
        disable_skan_campaign: row.disable_skan_campaign || false,
        postback_window_mode: row.postback_window_mode || null,
        special_industries: row.special_industries || [],
        po_number: row.po_number || '',
        rta_id: row.rta_id || '',
        rta_bid_enabled: row.rta_bid_enabled || false,
        rta_product_selection_enabled: row.rta_product_selection_enabled || false
      }

      // 同步辅助变量
      this.gamingComplianceAgreement = this.formData.gaming_ad_compliance_agreement
      this.appProfilePageState = this.formData.campaign_app_profile_page_state
      this.budgetAutoAdjustEnabled = this.formData.budget_auto_adjust_strategy || 'UNSET'

      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.campaignForm && this.$refs.campaignForm.clearValidate()
      })
    },

    async handleSubmit() {
      try {
        await this.$refs.campaignForm.validate()
        this.submitLoading = true


        // 构建请求数据
        const requestData = {
          advertiser_id: this.formData.advertiser_id,
          campaign_name: this.formData.campaign_name,
          objective_type: this.formData.objective_type,
          campaign_type: this.formData.campaign_type,
          budget_optimize_on: this.formData.budget_optimize_on,
          budget : this.formData.budget,
        }
        requestData.budget_mode = 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET'
        // 预算相关
        if (this.formData.budget_optimize_on) {
          requestData.budget_mode = this.formData.budget_mode
          if (this.formData.budget_mode !== 'BUDGET_MODE_INFINITE') {
            requestData.budget = this.formData.budget
          }
          if (this.formData.budget_mode === 'BUDGET_MODE_DYNAMIC_DAILY_BUDGET') {
            requestData.budget_auto_adjust_strategy = this.formData.budget_auto_adjust_strategy
          }
        }

        // 特殊行业
        if (this.formData.special_industries && this.formData.special_industries.length) {
          requestData.special_industries = this.formData.special_industries
        }

        // 应用推广相关
        if (this.formData.objective_type === 'APP_PROMOTION') {
          requestData.app_promotion_type = this.formData.app_promotion_type

          // iOS 14 系列特有字段
          if (this.formData.campaign_type === 'IOS14_CAMPAIGN') {
            if (this.formData.app_promotion_type === 'APP_INSTALL') {
              requestData.gaming_ad_compliance_agreement = this.formData.gaming_ad_compliance_agreement
            }
            requestData.campaign_app_profile_page_state = this.formData.campaign_app_profile_page_state
            if (this.formData.campaign_app_profile_page_state === 'ON') {
              requestData.campaign_app_profile_page_id = this.formData.campaign_app_profile_page_id
            }
            requestData.disable_skan_campaign = this.formData.disable_skan_campaign
          }
        }




        // SKAN 回传窗口模式
        if (this.formData.campaign_type === 'IOS14_CAMPAIGN' &&
            this.formData.operation_status === 'DISABLE' &&
            this.formData.postback_window_mode) {
          requestData.postback_window_mode = this.formData.postback_window_mode
        }

        // 非编辑模式添加 request_id
        if (!this.isEdit) {
          requestData.request_id = Date.now().toString()
        } else {
          requestData.campaign_id = this.editCampaignId
        }

        let res
        if (this.isEdit) {
          res = await campaignApi.update(requestData)
        } else {
          res = await campaignApi.create(requestData)
        }

        if (res.code === 0) {
          this.$message.success(this.isEdit ? '编辑成功' : '创建成功')
          this.dialogVisible = false
          await this.fetchCampaigns()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (err) {
        if (err !== 'cancel') {
          this.$message.error(err.message || '操作失败')
        }
      } finally {
        this.submitLoading = false
      }
    },

    handlePause(row) {
      this.$confirm('确定要暂停该广告系列吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const advertiserId = localStorage.getItem('advertiser_id')
          const res = await campaignApi.updateStatus({
            advertiserId,
            campaign_ids: [row.campaign_id],
            operation_status: 'DISABLE'
          })
          if (res.code === 0) {
            this.$message.success('已暂停')
            await this.fetchCampaigns()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    handleResume(row) {
      this.$confirm('确定要启用该广告系列吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const advertiserId = localStorage.getItem('advertiser_id')
          const res = await campaignApi.updateStatus({
            advertiserId,
            campaign_ids: [row.campaign_id],
            operation_status: 'ENABLE'
          })
          if (res.code === 0) {
            this.$message.success('已启用')
            await this.fetchCampaigns()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    handleDelete(row) {
      this.$confirm('删除后无法恢复，确定要删除该广告系列吗？', '警告', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'error'
      }).then(async () => {
        try {
          const advertiserId = row.advertiser_id
          const res = await campaignApi.updateStatus({
            advertiser_id: advertiserId,
            campaign_ids: [row.campaign_id],
            operation_status: 'DELETE'
          })
          if (res.code === 0) {
            this.$message.success('已删除')
            await this.fetchCampaigns()
          }
        } catch (error) {
          this.$message.error('操作失败')
        }
      }).catch(() => {})
    },

    handleViewAdgroups(row) {
      this.$router.push({
        path: '/adgroupsIndex',
        query: { campaignId: row.campaign_id, campaignName: row.campaign_name }
      })
    },

    handleDialogClose() {
      this.formData = {}
      this.editCampaignId = null
    },

    formatBudget(budget) {
      if (budget === undefined || budget === null) return '-'
      return (budget ).toFixed(2)
    },

    formatTime(timeStr) {
      if (!timeStr) return '-'
      return timeStr.replace(' ', '\n')
    },

    getObjectiveLabel(type) {
      return OBJECTIVE_LABEL[type] || type || '-'
    },

    getBudgetModeLabel(mode) {
      return BUDGET_MODE_LABEL[mode] || mode || '-'
    },

    getAdgroupModeLabel(mode) {
      return ADGROUP_MODE_LABEL[mode] || mode || '-'
    }
  },
  filters: {
    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? str.substring(0, len) + '...' : str
    }
  }
}
</script>

<style scoped>
.campaign-list {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #1f2f3d;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.filter-card {
  margin-bottom: 16px;
}

.campaign-name {
  font-weight: 500;
  color: #303133;
}

.id-text {
  font-size: 12px;
  color: #909399;
}

.budget-mode {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.time-text {
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-line;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}

/* 列配置弹窗样式 */
.column-config {
  max-height: 500px;
  overflow-y: auto;
}

.column-config-section {
  margin-bottom: 24px;
}

.column-config-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  border-left: 3px solid #409eff;
  padding-left: 10px;
}

.column-checkbox {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #ebeef5;
}

.column-checkbox:last-child {
  border-bottom: none;
}

.metric-desc {
  font-size: 11px;
  color: #909399;
  margin-left: 8px;
}

.metric-value {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
}
</style>